import { EditorSection } from '@/components/plot/editor/EditorSection';
import { FigurePanel, useFigureCompositionStore } from '@/store/figure-composition';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { ZoomIn, ArrowLeftRight, MoveVertical } from 'lucide-react';

interface PanelLegendAxesTabProps {
  panel: FigurePanel;
}

export function PanelLegendAxesTab({ panel }: PanelLegendAxesTabProps) {
  const currentComposition = useFigureCompositionStore((state) => state.currentComposition);
  const updatePanelOverrides = useFigureCompositionStore((state) => state.updatePanelOverrides);

  if (!currentComposition) return null;

  const axesSharing = currentComposition.axesSharing;
  const hasSharedAxes = axesSharing && (axesSharing.standardizeXScales || axesSharing.standardizeYScales);
  const axesOverride = panel.layoutOverrides?.axesOverride ?? false;
  const isAxisLocked = !!hasSharedAxes && !axesOverride;

  const handleZoomChange = (value: number[]) => {
    updatePanelOverrides(panel.id, { zoom: value[0] });
  };

  const handlePanXChange = (value: number[]) => {
    updatePanelOverrides(panel.id, { panX: value[0] });
  };

  const handlePanYChange = (value: number[]) => {
    updatePanelOverrides(panel.id, { panY: value[0] });
  };

  return (
    <div className="space-y-4">
      {panel.plotId && (
        <>
          <EditorSection
            id="panel-axes"
            title="Panel axes"
            description="Let this panel use its own axis settings."
          >
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="override-figure-axes"
                  checked={panel.layoutOverrides?.axesOverride ?? false}
                  onCheckedChange={(checked) =>
                    updatePanelOverrides(panel.id, { axesOverride: checked === true })
                  }
                />
                <Label htmlFor="override-figure-axes" className="text-sm cursor-pointer">
                  Use panel-specific axes
                </Label>
              </div>
              <p className="text-[10px] text-muted-foreground pl-6">
                Required before zoom and pan controls can be used.
              </p>
            </div>
          </EditorSection>

          <EditorSection
            id="view-zoom"
            title="View & zoom"
            description="Zoom or pan this panel without changing the data."
          >
            {isAxisLocked && (
              <div className="text-xs text-muted-foreground bg-muted p-2 rounded mb-3">
                Standardized axes are locked. Enable "Use panel-specific axes" to adjust this panel's view.
              </div>
            )}
            {!axesOverride && !isAxisLocked && (
              <div className="text-xs text-muted-foreground bg-muted p-2 rounded mb-3">
                Enable "Use panel-specific axes" to use zoom and pan controls.
              </div>
            )}
            <div className={`space-y-4 ${isAxisLocked || !axesOverride ? 'opacity-40 pointer-events-none' : ''}`}>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <ZoomIn className="h-3.5 w-3.5 text-muted-foreground" />
                  <Label className="text-xs">Zoom</Label>
                  <span className="text-xs ml-auto text-muted-foreground">
                    {((panel.layoutOverrides?.zoom ?? 1.0) * 100).toFixed(0)}%
                  </span>
                </div>
                <Slider
                  value={[panel.layoutOverrides?.zoom ?? 1.0]}
                  min={0.5}
                  max={2.0}
                  step={0.05}
                  onValueChange={handleZoomChange}
                  disabled={isAxisLocked || !axesOverride}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground px-1">
                  <span>50%</span>
                  <span>100%</span>
                  <span>200%</span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <ArrowLeftRight className="h-3.5 w-3.5 text-muted-foreground" />
                  <Label className="text-xs">Pan horizontally</Label>
                  <span className="text-xs ml-auto text-muted-foreground">
                    {((panel.layoutOverrides?.panX ?? 0) * 100).toFixed(0)}%
                  </span>
                </div>
                <Slider
                  value={[panel.layoutOverrides?.panX ?? 0]}
                  min={-1.0}
                  max={1.0}
                  step={0.01}
                  onValueChange={handlePanXChange}
                  disabled={isAxisLocked || !axesOverride}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground px-1">
                  <span>Left</span>
                  <span>Center</span>
                  <span>Right</span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <MoveVertical className="h-3.5 w-3.5 text-muted-foreground" />
                  <Label className="text-xs">Pan vertically</Label>
                  <span className="text-xs ml-auto text-muted-foreground">
                    {((panel.layoutOverrides?.panY ?? 0) * 100).toFixed(0)}%
                  </span>
                </div>
                <Slider
                  value={[panel.layoutOverrides?.panY ?? 0]}
                  min={-1.0}
                  max={1.0}
                  step={0.01}
                  onValueChange={handlePanYChange}
                  disabled={isAxisLocked || !axesOverride}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground px-1">
                  <span>Down</span>
                  <span>Center</span>
                  <span>Up</span>
                </div>
              </div>

              <div className="pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs w-full"
                  onClick={() => {
                    updatePanelOverrides(panel.id, { zoom: 1.0, panX: 0.0, panY: 0.0 });
                  }}
                  disabled={isAxisLocked || !axesOverride}
                >
                  Reset view
                </Button>
              </div>
            </div>
          </EditorSection>
        </>
      )}
    </div>
  );
}

